import { type FC } from "react";
import { Typography } from "@mui/material";
import { useTranslation } from "react-i18next";
import type { LinksItem } from "@/partner/entities/links";
import { useUser } from "@/partner/entities/auth";
import { formatMoney, formatNumber } from "@/partner/shared/utils/formatLocale";
import { LinkRewardTypeType } from "@/types/graphql/partner.generated";

interface LinkRewardSummaryProps {
  link: Pick<LinksItem, "reward" | "stats">;
}

/**
 * Условия награды ссылки (спека link-rewards, решение 6) строкой под
 * названием в таблице ссылок. Условия видит каждый, кто видит ссылку; сумма
 * к выплате, продажи, выручка и регистрации стоят колонками таблицы и
 * приходят только сотруднику с доступом к аналитике.
 */
export const LinkRewardSummary: FC<LinkRewardSummaryProps> = ({ link }) => {
  const { t, i18n } = useTranslation();
  const { user } = useUser();
  const { reward, stats } = link;
  const currency = stats?.currency ?? user.currentOrganization?.currency;
  const money = (value: number) =>
    currency
      ? formatMoney(value, currency, i18n.language)
      : formatNumber(value, i18n.language);

  const rewardTerms =
    reward?.type === LinkRewardTypeType.PercentType
      ? t("links.item.rewardPercent", {
          percent: formatNumber(reward.percent ?? 0, i18n.language),
        })
      : reward?.type === LinkRewardTypeType.FixedType
        ? t("links.item.rewardFixed", {
            // Своя валюта суммы (валюта организации на момент сохранения),
            // у ссылок до её появления текущая валюта организации.
            amount: reward.fixedCurrency
              ? formatMoney(
                  reward.fixedAmount ?? 0,
                  reward.fixedCurrency,
                  i18n.language
                )
              : money(reward.fixedAmount ?? 0),
          })
        : null;

  if (!rewardTerms) return null;

  return (
    <Typography variant="body2" color="text.secondary">
      {rewardTerms}
    </Typography>
  );
};
